import React, { useState, useEffect, useCallback } from 'react';
import { Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import AdminLogin from '../../components/AdminLogin';
import './AdminPortal.css';
import { logger } from '../../../../shared/utils/logger.js';

const API = process.env.REACT_APP_API_URL || '';
const LOG_MODULE = 'AdminPortal';
const ADMIN_SESSION_ME_PATH = '/v1/auth/session/me';
const ADMIN_LOGOUT_PATH = '/v1/auth/admin/logout';

/* ── Helpers ─────────────────────────────────────────── */
const adminFetch = async (path, opts = {}) => {
    const method = String(opts.method || 'GET').toUpperCase();
    logger.debug(LOG_MODULE, 'adminFetch started', {
        source: { file: 'features/auth/pages/AdminPortal/index.js', method: 'adminFetch' },
        path,
        httpMethod: method
    });

    let res;
    try {
        res = await fetch(`${API}${path}`, {
            ...opts,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(opts.headers || {}),
            },
        });
    } catch (error) {
        logger.error(LOG_MODULE, 'adminFetch network error', {
            source: { file: 'features/auth/pages/AdminPortal/index.js', method: 'adminFetch' },
            path,
            httpMethod: method,
            error: error instanceof Error
                ? { name: error.name, message: error.message, stack: error.stack }
                : String(error)
        });
        throw error;
    }

    logger.info(LOG_MODULE, 'adminFetch completed', {
        source: { file: 'features/auth/pages/AdminPortal/index.js', method: 'adminFetch' },
        path,
        httpMethod: method,
        status: res.status
    });

    if (res.status === 401 || res.status === 403) {
        logger.warn(LOG_MODULE, 'adminFetch unauthorized response', {
            source: { file: 'features/auth/pages/AdminPortal/index.js', method: 'adminFetch' },
            path,
            status: res.status
        });
        window.location.reload();
        return null;
    }

    try {
        return await res.json();
    } catch (error) {
        logger.error(LOG_MODULE, 'adminFetch JSON parse failed', {
            source: { file: 'features/auth/pages/AdminPortal/index.js', method: 'adminFetch' },
            path,
            status: res.status,
            error: error instanceof Error
                ? { name: error.name, message: error.message, stack: error.stack }
                : String(error)
        });
        throw error;
    }
};

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const Badge = ({ type, children }) => (
    <span className={`admin-badge ${type}`}>{children}</span>
);

const rowStatusBadge = (status) => {
    if (status === 1) return <Badge type="active">Active</Badge>;
    if (status === 3) return <Badge type="pending">Pending</Badge>;
    if (status === 4) return <Badge type="inactive">Declined</Badge>;
    if (status === 0) return <Badge type="pending">Inactive</Badge>;
    if (status === 2 || status === -1) return <Badge type="inactive">Deleted</Badge>;
    return <Badge type="inactive">{status ?? '—'}</Badge>;
};

function StoreDecisionModal({ mode, store, onClose, onDone }) {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const title = mode === 'approve' ? 'Approve Store' : 'Decline Store';

    const handleSubmit = async () => {
        setSubmitting(true);
        setError('');
        try {
            const endpoint = mode === 'approve'
                ? `/v1/admin/stores/${store.storeUuid}/approve`
                : `/v1/admin/stores/${store.storeUuid}/decline`;
            const response = await adminFetch(endpoint, {
                method: 'POST',
                body: JSON.stringify(reason ? { reason } : {}),
            });
            if (!response?.success) {
                setError(response?.message || 'Request failed');
                setSubmitting(false);
                return;
            }
            await onDone?.();
            setSubmitting(false);
            onClose();
        } catch (e) {
            setError('Request failed');
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-modal-overlay" onClick={onClose}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
                <h2>{title}</h2>
                <div className="admin-field" style={{ marginTop: 10 }}>
                    <label>Reason (optional)</label>
                    <input
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder={mode === 'decline' ? 'Why is this being declined?' : 'Optional note'}
                    />
                </div>
                {error ? <p className="admin-editor-error">{error}</p> : null}
                <div className="admin-modal-actions">
                    <button className="admin-btn admin-btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button
                        className={`admin-btn ${mode === 'approve' ? 'admin-btn-primary' : 'admin-btn-danger'}`}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? 'Working…' : mode === 'approve' ? 'Approve' : 'Decline'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── NAV ITEMS ───────────────────────────────────────── */
const NAV = [
    { key: 'dashboard', icon: '📊', label: 'Dashboard' },
    { key: 'stores', icon: '🏪', label: 'Stores' },
    { key: 'users', icon: '👤', label: 'Users' },
    { key: 'malls', icon: <Building2 size={16} />, label: 'Malls' },
    { key: 'sections', icon: '📋', label: 'Sections' },
    { key: 'queues', icon: '🎫', label: 'Queues' },
    { key: 'appointments', icon: '📅', label: 'Appointments' },
    { key: 'logs', icon: '📝', label: 'Logs' },
    { key: 'sessions', icon: '🔐', label: 'Sessions' },
];

/* ══════════════════════════════════════════════════════
   DASHBOARD
   ══════════════════════════════════════════════════════ */
function DashboardPage() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        (async () => {
            const [stores, users, queues, malls, sections] = await Promise.all([
                adminFetch('/v1/admin/stores?size=1'),
                adminFetch('/v1/admin/users?size=1'),
                adminFetch('/v1/admin/queues?size=1'),
                adminFetch('/v1/admin/malls?size=1'),
                adminFetch('/v1/admin/sections?size=1'),
            ]);
            setStats({
                stores: stores?.data?.totalElements ?? 0,
                users: users?.data?.totalElements ?? 0,
                queues: queues?.data?.totalElements ?? 0,
                malls: malls?.data?.totalElements ?? 0,
                sections: sections?.data?.totalElements ?? 0,
            });
        })();
    }, []);

    if (!stats) return <div className="admin-loading"><div className="admin-spinner" /> Loading dashboard…</div>;

    return (
        <>
            <div className="admin-page-header">
                <div>
                    <h1>Dashboard</h1>
                    <p className="subtitle">System overview at a glance</p>
                </div>
            </div>
            <div className="admin-stats-row">
                {Object.entries(stats).map(([key, val]) => (
                    <div className="admin-stat-card" key={key}>
                        <div className="label">{key}</div>
                        <div className="value">{val.toLocaleString()}</div>
                    </div>
                ))}
            </div>
        </>
    );
}

/* ══════════════════════════════════════════════════════
   GENERIC DATA TABLE
   ══════════════════════════════════════════════════════ */
function DataTable({ title, subtitle, endpoint, columns, actions, renderRow }) {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const pageSize = 20;

    const loadData = useCallback(async () => {
        setLoading(true);
        const joiner = endpoint.includes('?') ? '&' : '?';
        const res = await adminFetch(`${endpoint}${joiner}page=${page}&size=${pageSize}`);
        setData(res?.data?.content || []);
        setTotal(res?.data?.totalElements || 0);
        setLoading(false);
    }, [endpoint, page]);

    useEffect(() => { loadData(); }, [loadData]);

    const filtered = search
        ? data.filter((row) =>
            Object.values(row).some((v) =>
                String(v ?? '').toLowerCase().includes(search.toLowerCase())
            )
        )
        : data;

    const showHeader = Boolean(title || subtitle);

    return (
        <>
            {showHeader && (
                <div className="admin-page-header">
                    <div>
                        {title ? <h1>{title}</h1> : null}
                        {subtitle ? <p className="subtitle">{subtitle}</p> : null}
                    </div>
                </div>
            )}
            <div className="admin-table-container">
                <div className="admin-table-toolbar">
                    <input
                        className="admin-search-input"
                        placeholder="Search this page…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#71717a' }}>
                        {total.toLocaleString()} total
                    </span>
                </div>
                {loading ? (
                    <div className="admin-loading"><div className="admin-spinner" /> Loading…</div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}
                                {actions && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row, i) => (
                                <tr key={row.id || i}>
                                    {renderRow
                                        ? renderRow(row)
                                        : columns.map((c) => (
                                            <td key={c.key}>
                                                {c.render ? c.render(row[c.key], row) : String(row[c.key] ?? '—')}
                                            </td>
                                        ))}
                                    {actions && <td>{actions(row, loadData)}</td>}
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={columns.length + (actions ? 1 : 0)} style={{ textAlign: 'center', padding: 32, color: '#52525b' }}>No data found</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
                <div className="admin-pagination">
                    <span className="info">
                        Page {page + 1} of {Math.max(1, Math.ceil(total / pageSize))}
                    </span>
                    <div className="controls">
                        <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={14} /> Prev</button>
                        <button disabled={(page + 1) * pageSize >= total} onClick={() => setPage((p) => p + 1)}>Next <ChevronRight size={14} /></button>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ══════════════════════════════════════════════════════
   STORES PAGE
   ══════════════════════════════════════════════════════ */
function StoresPage() {
    const [detail, setDetail] = useState(null);
    const [decision, setDecision] = useState(null);
    const [filter, setFilter] = useState('pending');

    const columns = [
        { key: 'storeName', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'city', label: 'City' },
        { key: 'currentPlan', label: 'Plan', render: (v) => <Badge type="info">{v || 'FREE'}</Badge> },
        { key: 'rowStatus', label: 'Status', render: (v) => rowStatusBadge(v) },
        { key: 'createdAt', label: 'Created', render: (v) => formatDate(v) },
    ];

    const endpoint = filter === 'pending'
        ? '/v1/admin/stores?rowStatus=3'
        : '/v1/admin/stores';

    return (
        <>
            <div className="admin-page-header">
                <div>
                    <h1>Stores</h1>
                    <p className="subtitle">Approve or decline new store registrations</p>
                </div>
            </div>

            <div className="admin-tab-row">
                <button
                    className={`admin-tab-btn ${filter === 'pending' ? 'active' : ''}`}
                    onClick={() => {
                        setFilter('pending');
                        setDetail(null);
                        setDecision(null);
                    }}
                >
                    Pending approvals
                </button>
                <button
                    className={`admin-tab-btn ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => {
                        setFilter('all');
                        setDetail(null);
                        setDecision(null);
                    }}
                >
                    All stores
                </button>
            </div>

            <DataTable
                key={endpoint}
                title=""
                subtitle=""
                endpoint={endpoint}
                columns={columns}
                actions={(row, reload) => (
                    <div style={{ display: 'flex', gap: 6 }}>
                        {row?.rowStatus === 3 && (
                            <>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={() => setDecision({ mode: 'approve', row, reload })}
                                >
                                    Approve
                                </button>
                                <button
                                    className="admin-btn admin-btn-danger"
                                    onClick={() => setDecision({ mode: 'decline', row, reload })}
                                >
                                    Decline
                                </button>
                            </>
                        )}
                        <button className="admin-btn admin-btn-ghost" onClick={() => setDetail(row)}>View</button>
                    </div>
                )}
            />
            {detail && (
                <div className="admin-modal-overlay" onClick={() => setDetail(null)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{detail.storeName}</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            {['storeUuid', 'email', 'phone', 'city', 'area', 'state', 'pincode', 'currentPlan', 'clientType', 'storeCode'].map((f) => (
                                <div className="admin-field" key={f}>
                                    <label>{f}</label>
                                    <input value={detail[f] || ''} disabled />
                                </div>
                            ))}
                        </div>
                        <h3 style={{ fontSize: 14, color: '#a78bfa', marginTop: 16 }}>Feature Flags</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            {[
                                'queueManagementEnabled',
                                'appointmentsEnabled',
                                'servicesEnabled',
                                'staffingEnabled',
                                'offersEnabled',
                                'heatmapEnabled',
                                'queueAnalyticsEnabled',
                                'peakAnalyticsEnabled',
                                'abandonmentAnalyticsEnabled',
                                'bottleneckAnalyticsEnabled',
                                'offersAnalyticsEnabled',
                                'appointmentsAnalyticsEnabled',
                                'customerBehaviorAnalyticsEnabled',
                                'staffingAnalyticsEnabled',
                                'whatIfAnalyticsEnabled',
                                'anomalyAnalyticsEnabled',
                                'priorityPassEnabled',
                                'multiStoreEnabled'
                            ].map((f) => (
                                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#d4d4d8' }}>
                                    <label className="admin-toggle">
                                        <input type="checkbox" checked={detail[f] || false} readOnly />
                                        <span className="slider" />
                                    </label>
                                    {f.replace('Enabled', '').replace(/([A-Z])/g, ' $1').trim()}
                                </div>
                            ))}
                        </div>
                        <div className="admin-modal-actions">
                            <button className="admin-btn admin-btn-ghost" onClick={() => setDetail(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {decision && (
                <StoreDecisionModal
                    mode={decision.mode}
                    store={decision.row}
                    onClose={() => setDecision(null)}
                    onDone={async () => {
                        await decision.reload();
                    }}
                />
            )}
        </>
    );
}

/* ══════════════════════════════════════════════════════
   USERS PAGE
   ══════════════════════════════════════════════════════ */
function UsersPage() {
    const columns = [
        { key: 'firstname', label: 'First Name' },
        { key: 'lastname', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        {
            key: 'status', label: 'Status', render: (v) => {
                const type = v === 'ACTIVE' ? 'active' : v === 'PENDING' ? 'pending' : 'inactive';
                return <Badge type={type}>{v || '—'}</Badge>;
            }
        },
        { key: 'authProvider', label: 'Provider', render: (v) => <Badge type="info">{v || 'MACHINE'}</Badge> },
        { key: 'createdAt', label: 'Created', render: (v) => formatDate(v) },
    ];

    return (
        <DataTable
            title="Users"
            subtitle="All registered users"
            endpoint="/v1/admin/users"
            columns={columns}
        />
    );
}

/* ══════════════════════════════════════════════════════
   MALLS PAGE
   ══════════════════════════════════════════════════════ */
function MallsPage() {
    const columns = [
        { key: 'mallName', label: 'Name' },
        { key: 'city', label: 'City' },
        { key: 'area', label: 'Area' },
        { key: 'state', label: 'State' },
        { key: 'numberOfFloors', label: 'Floors' },
        { key: 'rowStatus', label: 'Status', render: (v) => rowStatusBadge(v) },
        { key: 'createdAt', label: 'Created', render: (v) => formatDate(v) },
    ];

    return (
        <DataTable
            title="Malls"
            subtitle="All malls in the system"
            endpoint="/v1/admin/malls"
            columns={columns}
        />
    );
}

/* ══════════════════════════════════════════════════════
   SECTIONS PAGE
   ══════════════════════════════════════════════════════ */
function SectionsPage() {
    const columns = [
        { key: 'sectionName', label: 'Name' },
        { key: 'sectionType', label: 'Type', render: (v) => <Badge type="info">{v || '—'}</Badge> },
        { key: 'storeUuid', label: 'Store UUID' },
        { key: 'floorNumber', label: 'Floor' },
        { key: 'sectionWindows', label: 'Windows' },
        { key: 'rowStatus', label: 'Status', render: (v) => rowStatusBadge(v) },
        { key: 'createdAt', label: 'Created', render: (v) => formatDate(v) },
    ];

    return (
        <DataTable
            title="Sections"
            subtitle="All queue/billing/trial sections"
            endpoint="/v1/admin/sections"
            columns={columns}
        />
    );
}

/* ══════════════════════════════════════════════════════
   QUEUES PAGE
   ══════════════════════════════════════════════════════ */
function QueuesPage() {
    const columns = [
        { key: 'queueUuid', label: 'Queue UUID' },
        { key: 'phoneNumber', label: 'Phone' },
        { key: 'position', label: 'Pos' },
        {
            key: 'status', label: 'Status', render: (v) => {
                const type = v === 'COMPLETED' ? 'active' : v === 'WAITING' ? 'pending' : v === 'ABANDONED' ? 'inactive' : 'info';
                return <Badge type={type}>{v || '—'}</Badge>;
            }
        },
        { key: 'estimatedWaitTime', label: 'Wait (min)' },
        { key: 'storeUuid', label: 'Store UUID' },
        { key: 'createdAt', label: 'Created', render: (v) => formatDate(v) },
    ];

    return (
        <DataTable
            title="Queue Entries"
            subtitle="All queue entries across stores"
            endpoint="/v1/admin/queues"
            columns={columns}
        />
    );
}

/* ══════════════════════════════════════════════════════
   APPOINTMENTS PAGE
   ══════════════════════════════════════════════════════ */
function AppointmentsPage() {
    const [tab, setTab] = useState('bookings');

    const bookingCols = [
        { key: 'customerName', label: 'Customer' },
        { key: 'customerPhone', label: 'Phone' },
        { key: 'status', label: 'Status', render: (v) => <Badge type={v === 'CONFIRMED' ? 'active' : 'pending'}>{v}</Badge> },
        { key: 'appointmentStartTime', label: 'Start', render: (v) => formatDate(v) },
        { key: 'storeUuid', label: 'Store UUID' },
    ];

    const serviceCols = [
        { key: 'serviceName', label: 'Service' },
        { key: 'appointmentMode', label: 'Mode' },
        { key: 'appointmentDurationMins', label: 'Duration (min)' },
        { key: 'active', label: 'Active', render: (v) => <Badge type={v ? 'active' : 'inactive'}>{v ? 'Yes' : 'No'}</Badge> },
        { key: 'storeUuid', label: 'Store UUID' },
    ];

    const settingsCols = [
        { key: 'storeUuid', label: 'Store UUID' },
        { key: 'id', label: 'ID' },
    ];

    return (
        <>
            <div className="admin-page-header">
                <div>
                    <h1>Appointments</h1>
                    <p className="subtitle">Bookings, services, and settings</p>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['bookings', 'services', 'settings'].map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        className={`admin-btn ${tab === t ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
                        style={{ textTransform: 'capitalize' }}>{t}</button>
                ))}
            </div>
            {tab === 'bookings' && <DataTable title="" subtitle="" endpoint="/v1/admin/appointments/bookings" columns={bookingCols} />}
            {tab === 'services' && <DataTable title="" subtitle="" endpoint="/v1/admin/appointments/services" columns={serviceCols} />}
            {tab === 'settings' && <DataTable title="" subtitle="" endpoint="/v1/admin/appointments/settings" columns={settingsCols} />}
        </>
    );
}

/* ══════════════════════════════════════════════════════
   LOGS PAGE
   ══════════════════════════════════════════════════════ */
function LogsPage() {
    const [tab, setTab] = useState('audit');

    const auditCols = [
        { key: 'entityType', label: 'Entity' },
        { key: 'action', label: 'Action', render: (v) => <Badge type="info">{v}</Badge> },
        { key: 'entityUuid', label: 'Entity UUID' },
        { key: 'actorRole', label: 'Actor Role' },
        { key: 'description', label: 'Description' },
        { key: 'createdAt', label: 'Time', render: (v) => formatDate(v) },
    ];

    const emailCols = [
        { key: 'id', label: 'ID' },
        { key: 'recipientEmail', label: 'To' },
        { key: 'subject', label: 'Subject' },
        { key: 'status', label: 'Status' },
    ];

    const apiCols = [
        { key: 'id', label: 'ID' },
        { key: 'method', label: 'Method' },
        { key: 'endpoint', label: 'Endpoint' },
        { key: 'statusCode', label: 'Status' },
    ];

    return (
        <>
            <div className="admin-page-header">
                <div>
                    <h1>Logs</h1>
                    <p className="subtitle">System audit trail and communication logs</p>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['audit', 'email', 'api'].map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        className={`admin-btn ${tab === t ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
                        style={{ textTransform: 'capitalize' }}>{t} Logs</button>
                ))}
            </div>
            {tab === 'audit' && <DataTable title="" subtitle="" endpoint="/v1/admin/logs/audit" columns={auditCols} />}
            {tab === 'email' && <DataTable title="" subtitle="" endpoint="/v1/admin/logs/email" columns={emailCols} />}
            {tab === 'api' && <DataTable title="" subtitle="" endpoint="/v1/admin/logs/api" columns={apiCols} />}
        </>
    );
}

/* ══════════════════════════════════════════════════════
   SESSIONS PAGE
   ══════════════════════════════════════════════════════ */
function SessionsPage() {
    const columns = [
        { key: 'id', label: 'ID' },
        { key: 'sessionId', label: 'Session ID' },
        { key: 'userId', label: 'User ID' },
        { key: 'deviceType', label: 'Device' },
        { key: 'createdAt', label: 'Created', render: (v) => formatDate(v) },
        { key: 'expiresAt', label: 'Expires', render: (v) => formatDate(v) },
    ];

    return (
        <DataTable
            title="Sessions"
            subtitle="Active user sessions"
            endpoint="/v1/admin/sessions"
            columns={columns}
        />
    );
}

/* ══════════════════════════════════════════════════════
   PAGE MAP
   ══════════════════════════════════════════════════════ */
const PAGES = {
    dashboard: DashboardPage,
    stores: StoresPage,
    users: UsersPage,
    malls: MallsPage,
    sections: SectionsPage,
    queues: QueuesPage,
    appointments: AppointmentsPage,
    logs: LogsPage,
    sessions: SessionsPage,
};

/* ══════════════════════════════════════════════════════
   MAIN ADMIN PORTAL
   ══════════════════════════════════════════════════════ */
export default function AdminPortal() {
    const [authed, setAuthed] = useState(false);
    const [bootstrapping, setBootstrapping] = useState(true);
    const [activePage, setActivePage] = useState('dashboard');

    const bootstrapAdminSession = useCallback(async () => {
        try {
            const response = await fetch(`${API}${ADMIN_SESSION_ME_PATH}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                setAuthed(false);
                return false;
            }

            const payload = await response.json();
            const role = String(payload?.data?.role || '');
            const isAdmin = role.toUpperCase() === 'ZOPEO_ADMIN';
            setAuthed(isAdmin);
            return isAdmin;
        } catch (error) {
            setAuthed(false);
            return false;
        } finally {
            setBootstrapping(false);
        }
    }, []);

    useEffect(() => {
        bootstrapAdminSession();
    }, [bootstrapAdminSession]);

    const handleLogout = async () => {
        try {
            await fetch(`${API}${ADMIN_LOGOUT_PATH}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
        } catch (error) {
            // Clear UI state even if network logout fails.
        }
        setAuthed(false);
    };

    if (bootstrapping) {
        return <div className="admin-loading"><div className="admin-spinner" /> Restoring admin session…</div>;
    }

    if (!authed) {
        return <AdminLogin onLogin={() => bootstrapAdminSession()} />;
    }

    const PageComponent = PAGES[activePage] || DashboardPage;

    return (
        <div className="admin-layout">
            {/* Sidebar */}
            <aside className="admin-sidebar">
                <div className="admin-sidebar-brand">
                    <h2>Z<span className="brand-q">o</span>pe<span className="brand-q">o</span></h2>
                    <span className="badge">ADMIN</span>
                </div>
                <nav className="admin-sidebar-nav">
                    {NAV.map((item) => (
                        <button
                            key={item.key}
                            className={`admin-nav-item ${activePage === item.key ? 'active' : ''}`}
                            onClick={() => setActivePage(item.key)}
                        >
                            <span className="icon">{item.icon}</span>
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="admin-sidebar-footer">
                    <button className="admin-logout-btn" onClick={handleLogout}>
                        <span>🚪</span>
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main className="admin-main">
                <PageComponent />
            </main>
        </div>
    );
}
